import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'

import { useAppSelector } from '@/stores/hooks.ts'
import { sbpPaymentSelectors } from '@/stores/slices/sbp-payments-slice.ts'
import { B2C_TRANSFER_ROOT } from '@/services/b2c.ts'
import { B2CPaymentsRequest } from '@/@types/b2c.types.ts'

import { RefundOperationParams, SbpPayment, SbpRefundOperation, TableData } from '../types.ts'
import { omitRequestParams } from '../utils.ts'

import { apiService } from './root.ts'

export const SBP_ROOT = '/sbp-payments'

export const SBP_ENDPOINTS = {
  list_to_b: SBP_ROOT,
  list_to_c: `${B2C_TRANSFER_ROOT}/requests`,
  item: (id: string) => `${SBP_ROOT}/${id}`,
  reverseList: (id: string | number) => `${SBP_ROOT}/${id}/reverseList`,
  reverse: (id: string) => `${SBP_ROOT}/${id}/reverse`,
  reverseConfirm: `${SBP_ROOT}/reverseConfirm`,
}

export const useGetSbpPaymentsByMethods2B = () => {
  const { page, showCount, sort: [sortField, direction] = [] } = useAppSelector(sbpPaymentSelectors.filters)
  const { dateFrom, dateTo, operationState, requestId, type } = useAppSelector(sbpPaymentSelectors.paramsForRequest)

  const params = { dateFrom, dateTo, requestId, operationState, type }

  const queryParams = omitRequestParams({
    page,
    showCount,
    sortField,
    direction,
    ...params,
  })

  return useQuery({
    placeholderData: keepPreviousData,
    queryKey: ['sbp_list_to_b', queryParams],
    queryFn: () =>
      apiService.get<TableData<SbpPayment>>(SBP_ENDPOINTS.list_to_b, {
        queryParams,
      }),
  })
}

export const useGetSbpPaymentsByMethods2C = () => {
  const { page, showCount } = useAppSelector(sbpPaymentSelectors.filters)
  const { dateFrom, dateTo, reference, transferState } = useAppSelector(sbpPaymentSelectors.paramsForRequest)

  const params = { dateFrom, dateTo, reference, transferState }

  const queryParams = omitRequestParams({
    page,
    showCount,
    ...params,
  })

  return useQuery({
    placeholderData: keepPreviousData,
    queryKey: ['sbp_list_to_c', queryParams],
    queryFn: () =>
      apiService.get<TableData<B2CPaymentsRequest>>(SBP_ENDPOINTS.list_to_c, {
        queryParams,
      }),
  })
}

export const useGetSbpPayment = (id: string) =>
  useQuery({
    queryKey: ['sbp-item', { id }],
    queryFn: () => apiService.get<SbpPayment>(SBP_ENDPOINTS.item(id)),
  })

export const useGetReverseList = (payment: SbpPayment) =>
  useQuery({
    queryKey: ['reverseList', { id: payment.operationId }],
    queryFn: () => apiService.get<Array<SbpRefundOperation>>(SBP_ENDPOINTS.reverseList(payment.operationId)),
    select: (response) => ({
      ...response,
      data: response.data.map((refundItem) => ({
        ...refundItem,
        sndPam: payment.sndPam,
        sndPhone: payment.sndPhone,
        tspName: payment.tspName,
      })) as Array<SbpRefundOperation>,
    }),
  })

export const useMutateSbpRefund = (paymentId: string | number) =>
  useMutation({
    mutationFn: (bodyParams: RefundOperationParams) =>
      apiService.post<{ state: 'ERROR' | 'OK'; reversedOperationId: null | number; errorMessage: string | null }>(
        SBP_ENDPOINTS.reverse(paymentId.toString()),
        {
          bodyParams,
        }
      ),
  })
